import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  ArrowLeft, Stethoscope, Building2, Search, CheckCircle, XCircle,
  Clock, FileText, Eye, ExternalLink, Mail, Phone, MapPin, Shield,
  AlertTriangle, Users, Calendar, Syringe, TestTube, Trash2
} from "lucide-react";

export default function AdminVets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vets, setVets] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  
  // Detail dialog
  const [selectedVet, setSelectedVet] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Verification dialog
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyAction, setVerifyAction] = useState(null); // 'approve' or 'reject'
  const [verifyNotes, setVerifyNotes] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Accesso non autorizzato");
      navigate('/dashboard');
      return;
    }
    fetchVets();
  }, [user, statusFilter, page]);

  const fetchVets = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/vets?page=${page}&limit=20`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setVets(data.vets || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching vets:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchVets();
  };

  const openDetail = async (vet) => {
    try {
      const res = await authFetch(`${API}/admin/vets/${vet.vet_id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedVet(data);
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dettagli");
    }
  };

  const openVerifyDialog = (action) => {
    setVerifyAction(action);
    setVerifyNotes("");
    setShowVerifyDialog(true);
  };

  const handleVerify = async () => {
    if (!selectedVet || !verifyAction) return;

    setVerifying(true);
    try {
      let url = `${API}/admin/vets/${selectedVet.vet_id}/verify?action=${verifyAction}`;
      if (verifyNotes.trim()) {
        url += `&notes=${encodeURIComponent(verifyNotes)}`;
      }

      const res = await authFetch(url, { method: 'POST' });
      if (res.ok) {
        toast.success(verifyAction === 'approve' ? "Account approvato!" : "Account rifiutato");
        setShowVerifyDialog(false);
        setShowDetailDialog(false);
        fetchVets();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async (vetId, email) => {
    if (!confirm(`Eliminare definitivamente l'account di ${email}?`)) return;

    try {
      const res = await authFetch(`${API}/admin/vets/${vetId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success("Account eliminato");
        setShowDetailDialog(false);
        fetchVets();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const statusCounts = {
    pending: vets.filter(v => v.verification_status === 'pending').length,
    verified: vets.filter(v => v.verification_status === 'verified').length,
    rejected: vets.filter(v => v.verification_status === 'rejected').length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F] pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-[#1D1D1F]/90 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/dashboard')} 
              className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1D1D1F] dark:text-white">Verifica Veterinari</h1>
              <p className="text-sm text-[#86868B]">{total} account registrati</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-4 border border-black/5 dark:border-white/10 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Status Tabs */}
            <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }} className="w-full sm:w-auto">
              <TabsList className="bg-[#F5F5F7] dark:bg-white/10 p-1 rounded-xl">
                <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  <Clock size={16} className="mr-2" /> In attesa
                </TabsTrigger>
                <TabsTrigger value="verified" className="rounded-lg data-[state=active]:bg-green-500 data-[state=active]:text-white">
                  <CheckCircle size={16} className="mr-2" /> Verificati
                </TabsTrigger>
                <TabsTrigger value="rejected" className="rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white">
                  <XCircle size={16} className="mr-2" /> Rifiutati
                </TabsTrigger>
                <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white">
                  Tutti
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome, email, codice albo, P.IVA..."
                  className="h-10 pl-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 border-0"
                />
              </div>
              <Button type="submit" className="rounded-xl bg-[#2E7D32] hover:bg-[#1B5E20]">
                Cerca
              </Button>
            </form>
          </div>
        </div>

        {/* Vets List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-[#2E7D32] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : vets.length === 0 ? (
          <div className="bg-white dark:bg-white/5 rounded-2xl p-12 text-center border border-black/5 dark:border-white/10">
            <Stethoscope className="mx-auto text-[#86868B] mb-4" size={48} />
            <h3 className="font-semibold text-[#1D1D1F] dark:text-white mb-2">Nessun veterinario trovato</h3>
            <p className="text-[#86868B]">Non ci sono account con questo filtro</p>
          </div>
        ) : (
          <div className="space-y-4">
            {vets.map(vet => (
              <div 
                key={vet.vet_id}
                className="bg-white dark:bg-white/5 rounded-2xl p-4 border border-black/5 dark:border-white/10 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openDetail(vet)}
                data-testid={`vet-card-${vet.vet_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    vet.account_type === 'clinica' ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-green-100 dark:bg-green-900/30'
                  }`}>
                    {vet.account_type === 'clinica' ? (
                      <Building2 className="text-orange-600" size={24} />
                    ) : (
                      <Stethoscope className="text-green-600" size={24} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#1D1D1F] dark:text-white">{vet.name}</h3>
                      
                      {/* Status Badge */}
                      {vet.verification_status === 'pending' && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs rounded-full flex items-center gap-1">
                          <Clock size={12} /> In attesa
                        </span>
                      )}
                      {vet.verification_status === 'verified' && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs rounded-full flex items-center gap-1">
                          <CheckCircle size={12} /> Verificato
                        </span>
                      )}
                      {vet.verification_status === 'rejected' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-xs rounded-full flex items-center gap-1">
                          <XCircle size={12} /> Rifiutato
                        </span>
                      )}

                      {/* Type Badge */}
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                        {vet.account_type === 'clinica' ? 'Clinica' : 'Veterinario'}
                      </span>
                    </div>

                    <p className="text-sm text-[#86868B] mt-1">{vet.email}</p>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#86868B]">
                      {vet.codice_albo && (
                        <span>Albo: {vet.codice_albo} ({vet.ordine_provincia})</span>
                      )}
                      {vet.partita_iva && (
                        <span>P.IVA: {vet.partita_iva}</span>
                      )}
                      {vet.city && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {vet.city}
                        </span>
                      )}
                      <span>Registrato: {formatDate(vet.created_at)}</span>
                    </div>

                    {/* Documents indicator */}
                    <div className="flex gap-2 mt-2">
                      {vet.id_document_url && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded flex items-center gap-1">
                          <FileText size={12} /> ID Caricato
                        </span>
                      )}
                      {vet.certification_url && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 text-xs rounded flex items-center gap-1">
                          <Shield size={12} /> Certificazione
                        </span>
                      )}
                      {!vet.id_document_url && !vet.certification_url && (
                        <span className="px-2 py-0.5 bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 text-xs rounded flex items-center gap-1">
                          <AlertTriangle size={12} /> Nessun documento
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right hidden sm:block">
                    <p className="text-lg font-bold text-[#1D1D1F] dark:text-white">{vet.patients_count || 0}</p>
                    <p className="text-xs text-[#86868B]">pazienti</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button 
              variant="outline" 
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="rounded-xl"
            >
              Precedente
            </Button>
            <span className="px-4 py-2 text-[#86868B]">Pagina {page}</span>
            <Button 
              variant="outline" 
              disabled={vets.length < 20}
              onClick={() => setPage(p => p + 1)}
              className="rounded-xl"
            >
              Successiva
            </Button>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border-0">
          {selectedVet && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    selectedVet.account_type === 'clinica' ? 'bg-orange-100' : 'bg-green-100'
                  }`}>
                    {selectedVet.account_type === 'clinica' ? (
                      <Building2 className="text-orange-600" size={20} />
                    ) : (
                      <Stethoscope className="text-green-600" size={20} />
                    )}
                  </div>
                  <div>
                    <span className="text-xl">{selectedVet.name}</span>
                    <p className="text-sm font-normal text-[#86868B]">
                      {selectedVet.account_type === 'clinica' ? 'Clinica Veterinaria' : 'Veterinario'}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  {selectedVet.verification_status === 'pending' && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-2">
                      <Clock size={16} /> In attesa di verifica
                    </span>
                  )}
                  {selectedVet.verification_status === 'verified' && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-2">
                      <CheckCircle size={16} /> Verificato il {formatDate(selectedVet.verified_at)}
                    </span>
                  )}
                  {selectedVet.verification_status === 'rejected' && (
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium flex items-center gap-2">
                      <XCircle size={16} /> Rifiutato
                    </span>
                  )}
                </div>

                {selectedVet.verification_notes && (
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs text-[#86868B] mb-1">Note verifica:</p>
                    <p className="text-sm text-[#1D1D1F]">{selectedVet.verification_notes}</p>
                  </div>
                )}

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#F5F5F7] rounded-xl">
                    <p className="text-xs text-[#86868B] mb-1">Email</p>
                    <p className="text-sm text-[#1D1D1F] flex items-center gap-2">
                      <Mail size={14} /> {selectedVet.email}
                    </p>
                  </div>
                  <div className="p-3 bg-[#F5F5F7] rounded-xl">
                    <p className="text-xs text-[#86868B] mb-1">Telefono</p>
                    <p className="text-sm text-[#1D1D1F] flex items-center gap-2">
                      <Phone size={14} /> {selectedVet.phone || "—"}
                    </p>
                  </div>
                </div>

                {/* Professional Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedVet.codice_albo && (
                    <div className="p-3 bg-[#F5F5F7] rounded-xl">
                      <p className="text-xs text-[#86868B] mb-1">Codice Albo</p>
                      <p className="text-sm text-[#1D1D1F] font-mono">{selectedVet.codice_albo}</p>
                      <p className="text-xs text-[#86868B]">Ordine: {selectedVet.ordine_provincia}</p>
                    </div>
                  )}
                  {selectedVet.partita_iva && (
                    <div className="p-3 bg-[#F5F5F7] rounded-xl">
                      <p className="text-xs text-[#86868B] mb-1">Partita IVA</p>
                      <p className="text-sm text-[#1D1D1F] font-mono">{selectedVet.partita_iva}</p>
                      {selectedVet.codice_sdi && (
                        <p className="text-xs text-[#86868B]">SDI: {selectedVet.codice_sdi}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Address */}
                {(selectedVet.address || selectedVet.city) && (
                  <div className="p-3 bg-[#F5F5F7] rounded-xl">
                    <p className="text-xs text-[#86868B] mb-1">Indirizzo</p>
                    <p className="text-sm text-[#1D1D1F] flex items-center gap-2">
                      <MapPin size={14} /> 
                      {[selectedVet.address, selectedVet.city, selectedVet.postal_code, selectedVet.provincia].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}

                {/* Specializations */}
                {selectedVet.specializations?.length > 0 && (
                  <div>
                    <p className="text-xs text-[#86868B] mb-2">Specializzazioni</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedVet.specializations.map((spec, i) => (
                        <span key={i} className="px-2 py-1 bg-[#2E7D32]/10 text-[#2E7D32] text-xs rounded-lg">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                <div>
                  <p className="text-xs text-[#86868B] mb-2">Documenti caricati</p>
                  <div className="space-y-2">
                    {selectedVet.id_document_url ? (
                      <a 
                        href={`${API.replace('/api', '')}${selectedVet.id_document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                      >
                        <FileText className="text-blue-600" size={20} />
                        <span className="flex-1 text-sm text-blue-800">Documento d'identità</span>
                        <ExternalLink size={16} className="text-blue-600" />
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                        <AlertTriangle className="text-amber-600" size={20} />
                        <span className="text-sm text-amber-800">Documento d'identità non caricato</span>
                      </div>
                    )}

                    {selectedVet.certification_url ? (
                      <a 
                        href={`${API.replace('/api', '')}${selectedVet.certification_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
                      >
                        <Shield className="text-purple-600" size={20} />
                        <span className="flex-1 text-sm text-purple-800">Certificazione professionale</span>
                        <ExternalLink size={16} className="text-purple-600" />
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <Shield className="text-gray-400" size={20} />
                        <span className="text-sm text-gray-500">Certificazione non caricata</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-[#F5F5F7] rounded-xl text-center">
                    <Users className="mx-auto text-[#86868B] mb-1" size={20} />
                    <p className="text-xl font-bold text-[#1D1D1F]">{selectedVet.patients_count || 0}</p>
                    <p className="text-xs text-[#86868B]">Pazienti</p>
                  </div>
                  <div className="p-3 bg-[#F5F5F7] rounded-xl text-center">
                    <Syringe className="mx-auto text-[#86868B] mb-1" size={20} />
                    <p className="text-xl font-bold text-[#1D1D1F]">{selectedVet.vaccines_count || 0}</p>
                    <p className="text-xs text-[#86868B]">Vaccini</p>
                  </div>
                  <div className="p-3 bg-[#F5F5F7] rounded-xl text-center">
                    <TestTube className="mx-auto text-[#86868B] mb-1" size={20} />
                    <p className="text-xl font-bold text-[#1D1D1F]">{selectedVet.exams_count || 0}</p>
                    <p className="text-xs text-[#86868B]">Esami</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedVet.verification_status === 'pending' && (
                  <>
                    <Button 
                      onClick={() => openVerifyDialog('reject')}
                      variant="outline"
                      className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="reject-vet-btn"
                    >
                      <XCircle size={18} className="mr-2" /> Rifiuta
                    </Button>
                    <Button 
                      onClick={() => openVerifyDialog('approve')}
                      className="flex-1 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                      data-testid="approve-vet-btn"
                    >
                      <CheckCircle size={18} className="mr-2" /> Approva
                    </Button>
                  </>
                )}
                {selectedVet.verification_status !== 'pending' && (
                  <>
                    <Button 
                      onClick={() => handleDelete(selectedVet.vet_id, selectedVet.email)}
                      variant="outline"
                      className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 size={18} className="mr-2" /> Elimina account
                    </Button>
                    {selectedVet.verification_status === 'rejected' && (
                      <Button 
                        onClick={() => openVerifyDialog('approve')}
                        className="flex-1 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                      >
                        <CheckCircle size={18} className="mr-2" /> Approva
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Confirmation Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="max-w-md rounded-3xl border-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {verifyAction === 'approve' ? (
                <>
                  <CheckCircle className="text-green-600" size={24} />
                  <span>Approva account</span>
                </>
              ) : (
                <>
                  <XCircle className="text-red-600" size={24} />
                  <span>Rifiuta account</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-[#86868B] mb-4">
              {verifyAction === 'approve' 
                ? `Stai per approvare l'account di ${selectedVet?.name}. L'utente riceverà una notifica email.`
                : `Stai per rifiutare l'account di ${selectedVet?.name}. L'utente riceverà una notifica email.`
              }
            </p>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Note (opzionale)</label>
              <Textarea
                value={verifyNotes}
                onChange={(e) => setVerifyNotes(e.target.value)}
                placeholder={verifyAction === 'approve' 
                  ? "Es: Verificato con codice albo..."
                  : "Motivo del rifiuto..."
                }
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowVerifyDialog(false)}
              className="flex-1 rounded-xl"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleVerify}
              disabled={verifying}
              className={`flex-1 rounded-xl ${
                verifyAction === 'approve' 
                  ? 'bg-[#2E7D32] hover:bg-[#1B5E20]' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              data-testid="confirm-verify-btn"
            >
              {verifying ? "Elaborazione..." : (verifyAction === 'approve' ? "Conferma approvazione" : "Conferma rifiuto")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
